import React, { useCallback, useEffect, useState } from 'react';
import { Box, Paper, Skeleton, Typography } from '@mui/material';
import AppsIcon from '@mui/icons-material/Apps';
import { Company, CompanyApplication } from '../../../../../interfaces/users.interface';
import { CompanyTabListParams } from '../../../../../interfaces/companyDetail.interface';
import { getCompanyApps } from '../../../../../services/companyDetail';
import { useCompanyResource } from '../../../../Vendors/useCompanyResource';
import { DataCell, DataRow } from '../../../../Tables/DataList';
import { tightTableSx } from '../../../../Tables/dataTableStyles';
import { TabPagination } from '../../../../Vendors/companyTabUi';
import LicensedApplicationDataRowV2 from './LicensedApplicationDataRowV2';

const APPS_PER_PAGE = 5;

// Server-paginated licensed apps (application not owned by this company), each
// row carrying its embedded certifications — no full-list fetch.
const licensedFetcher = (id: number | string, params?: CompanyTabListParams) =>
  getCompanyApps(id, { ...params, kind: 'licensed' });

type LicensedApplicationsCardsProps = {
  company: Company;
  userCanAdminCompany: boolean;
  companyCallback: any;
  reloadToken?: number;
};

const LicensedApplicationsCards = (props: LicensedApplicationsCardsProps) => {
  const { company, userCanAdminCompany, companyCallback, reloadToken } = props;

  const [page, setPage] = useState(1);
  const { items: applications, count, loading, refetch } = useCompanyResource<CompanyApplication>(
    licensedFetcher,
    company.id,
    page,
    APPS_PER_PAGE,
  );

  // Re-fetch this page when the tab-level add modal reports a change.
  useEffect(() => {
    if (reloadToken) refetch();
  }, [reloadToken]);

  // Keep the parent in sync (header/labels) and refetch this page after a change.
  const handleChange = useCallback((updatedCompany: Company) => {
    companyCallback(updatedCompany);
    refetch();
  }, [companyCallback, refetch]);

  return (
    <Box sx={{ mt: 4 }}>
      <Typography variant='subtitle1' sx={{ fontWeight: 600, mb: 1.5, color: 'text.secondary', textTransform: 'uppercase', fontSize: '0.85rem', letterSpacing: '0.05em' }}>
        3rd Party Licensed Applications ({count})
      </Typography>

      {loading ? (
        <Skeleton variant='rectangular' height={160} sx={{ borderRadius: 2 }} />
      ) : applications.length > 0 ? (
        <Paper
          elevation={0}
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            overflow: 'hidden',
            ...tightTableSx,
          }}
        >
          <DataRow header sx={{ bgcolor: '#E7EAF3', marginLeft: 0, width: '100%' }}>
            <DataCell xs={1} textAlign='center'>Shield</DataCell>
            <DataCell xs={2}>Name</DataCell>
            <DataCell xs={1}>Version / Apps</DataCell>
            <DataCell xs={1.5}>Sites</DataCell>
            <DataCell xs={2}>Services</DataCell>
            <DataCell xs={1.5}>Configuration Guidelines</DataCell>
            <DataCell xs={1.5}>Application Types</DataCell>
            <DataCell xs={1}>&nbsp;</DataCell>
          </DataRow>
          {applications.map((companyApplication: CompanyApplication) => (
            <LicensedApplicationDataRowV2
              key={companyApplication.id}
              company={company}
              companyApplication={companyApplication}
              companySuccessCallback={handleChange}
              userCanAdminCompany={userCanAdminCompany}
            />
          ))}
        </Paper>
      ) : (
        <Paper
          elevation={0}
          sx={{
            border: '1px dashed',
            borderColor: 'divider',
            borderRadius: 2,
            p: 4,
            textAlign: 'center',
          }}
        >
          <AppsIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
          <Typography variant='body2' color='text.secondary'>
            No licensed applications
          </Typography>
        </Paper>
      )}

      <TabPagination count={count} page={page} pageSize={APPS_PER_PAGE} onChange={setPage} />
    </Box>
  );
};

export default LicensedApplicationsCards;
